import { Container, Flex } from '@grupoboticario/flora-react';
import { Route, Routes, useLocation } from 'react-router-dom';

import { ErrorBoundaries, PageInfo, ResponsiveFloraTypography } from 'components';
import { CadastralChange, PasswordValidate } from 'components/Drawers';
import { AddressInformation, AddressForm } from 'components/Drawers/CadastralChange/AddressChange';
import { AddressModalConfirmation } from 'components/Drawers/CadastralChange/AddressChange/components';
import {
  RegistrationError,
  RegistrationSuccess,
} from 'components/Drawers/CadastralChange/components';
import { EmailForm } from 'components/Drawers/CadastralChange/EmailChange';
import { PhoneForm } from 'components/Drawers/CadastralChange/PhoneChange';
import { BRANDS } from 'config/environments/shared';
import { UserHelpContact } from 'domains/user.d';
import { CadastralChangeEnum } from 'helpers';
import { useResellerDetail, useResellerSupervisor } from 'hooks';
import { useGlobalContext } from 'store/contexts';

import { Central, CustomerService, Detail, Help, Summary, Supervisor } from './components';
import Styles from './Profile.styles';

export const Profile = () => {
  const { isError: isErrorResellerDetail } = useResellerDetail();
  const { data: resellerHelpContact, isLoading: isLoadingResellerHelpContact } =
    useResellerSupervisor();
  const { pathname } = useLocation();
  const { brand: environmentBrand } = useGlobalContext();
  const isEud = BRANDS.EUD === environmentBrand.short;

  return (
    <>
      <Container>
        <PageInfo
          breadcrumbs={[
            {
              text: 'Perfil',
              url: pathname,
            },
          ]}
        >
          <Flex direction='column' gap='$2'>
            <ResponsiveFloraTypography fontSize='heading' as='h1' fontWeight='bold'>
              Perfil
            </ResponsiveFloraTypography>

            <ResponsiveFloraTypography>
              Confira informações da sua conta e encontre contatos de ajuda.
            </ResponsiveFloraTypography>
          </Flex>
        </PageInfo>
      </Container>

      <Styles.Wrapper data-autotagging-flora-section='perfil'>
        <Container size='large'>
          {isErrorResellerDetail ? (
            <ErrorBoundaries.ReloadPage title='Tivemos um problema para exibir seu perfil' />
          ) : (
            <>
              <Summary />

              <Styles.Divider variant='Line' />

              <Detail />

              <Styles.Divider variant='Line' />

              {isEud ? (
                <>
                  <Help />
                  <Styles.Divider variant='Line' />
                </>
              ) : null}

              {!isLoadingResellerHelpContact && Object.keys(resellerHelpContact || {}).length ? (
                <>
                  {resellerHelpContact?.hasOwnProperty(UserHelpContact.SUPERVISOR) ? (
                    <>
                      <Supervisor />
                      <Styles.Divider variant='Line' />
                    </>
                  ) : (
                    <>
                      <CustomerService />
                      <Styles.Divider variant='Line' />
                    </>
                  )}
                </>
              ) : null}

              <Central />
            </>
          )}
        </Container>
      </Styles.Wrapper>
      <Routes>
        <Route path='editar/*'>
          <Route path='celular/*'>
            <Route
              path='senha'
              index
              element={
                <CadastralChange type={CadastralChangeEnum.phone}>
                  <PasswordValidate
                    type={CadastralChangeEnum.phone}
                    nextStep='/perfil/editar/celular/confirmacao'
                  />
                </CadastralChange>
              }
            />
            <Route
              path='confirmacao'
              element={
                <CadastralChange type={CadastralChangeEnum.phone}>
                  <PhoneForm />
                </CadastralChange>
              }
            />
            <Route
              path='sucesso'
              element={
                <CadastralChange type={CadastralChangeEnum.phone}>
                  <RegistrationSuccess type='phone' />
                </CadastralChange>
              }
            />
            <Route
              path='erro/*'
              element={
                <CadastralChange type={CadastralChangeEnum.phone}>
                  <RegistrationError type='phone' />
                </CadastralChange>
              }
            />
          </Route>
          <Route path='endereco/*'>
            <Route
              path='senha'
              index
              element={
                <CadastralChange type={CadastralChangeEnum.address}>
                  <PasswordValidate
                    type={CadastralChangeEnum.address}
                    nextStep='/perfil/editar/endereco/informacoes-importantes'
                  />
                </CadastralChange>
              }
            />
            <Route
              path='informacoes-importantes'
              element={
                <CadastralChange type={CadastralChangeEnum.address}>
                  <AddressInformation />
                </CadastralChange>
              }
            />
            <Route
              path='confirmacao'
              element={
                <CadastralChange type={CadastralChangeEnum.address}>
                  <AddressForm />
                </CadastralChange>
              }
            />
            <Route
              path='modal-confirmacao'
              element={
                <CadastralChange type={CadastralChangeEnum.address}>
                  <AddressModalConfirmation />
                </CadastralChange>
              }
            />
            <Route
              path='sucesso'
              element={
                <CadastralChange type='address'>
                  <RegistrationSuccess type='address' />
                </CadastralChange>
              }
            />
            <Route
              path='erro'
              element={
                <CadastralChange type='address'>
                  <RegistrationError type='address' />
                </CadastralChange>
              }
            />
          </Route>
          <Route path='email/*'>
            <Route
              path='senha'
              index
              element={
                <CadastralChange type={CadastralChangeEnum.email}>
                  <PasswordValidate
                    type={CadastralChangeEnum.email}
                    nextStep='/perfil/editar/email/confirmacao'
                  />
                </CadastralChange>
              }
            />
            <Route
              path='confirmacao'
              element={
                <CadastralChange type={CadastralChangeEnum.email}>
                  <EmailForm />
                </CadastralChange>
              }
            />
            <Route
              path='sucesso'
              element={
                <CadastralChange type={CadastralChangeEnum.email}>
                  <RegistrationSuccess type='email' />
                </CadastralChange>
              }
            />
            <Route
              path='erro/*'
              element={
                <CadastralChange type={CadastralChangeEnum.email}>
                  <RegistrationError type='email' />
                </CadastralChange>
              }
            />
          </Route>
        </Route>
      </Routes>
    </>
  );
};

export default Profile;
